import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
// Phase 247 — Defensive title-case for the welcome header so operator typos
// like "kalyna" render as "Kalyna" WITHOUT mutating the underlying user doc.
import { formatPersonName } from '@/utils/textFormatters';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  LayoutDashboard, Users, Briefcase, CalendarDays, IndianRupee, Video, Apple,
  Menu, LogOut, Sun, Moon, ChevronLeft, ChevronRight, Trophy, Settings, Bell, ShieldCheck, Dumbbell,
  User, CreditCard, Download, MessageSquare, Building2, BarChart3, Target, Clock, Ticket, HelpCircle, GraduationCap, ClipboardCheck, Inbox, Shield, Megaphone, Hexagon, Crown, Sparkles, Heart, Activity, Bot, RefreshCw, Radio, BookOpen, Webhook, Network, Globe, FileText, Workflow, Camera, Trash2, Loader2, Layers
} from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';
import { usePwaInstall } from '@/lib/pwaInstall';
import PwaInstallBanner from '@/components/PwaInstallBanner';
import { bestPhotoFor, resolvePhotoUrl } from '@/lib/photo';
import { NotificationBell } from '@/components/NotificationBell';
import SystemHealthBadge from '@/components/SystemHealthBadge';
import { TrialBanner, SuspensionOverlay } from '@/components/TrialBanner';
// Phase 255f — LivexGlobalSubscriptionBanner intentionally NOT mounted in AppShell.
// Dunning is surfaced page-locally inside LivexDashboard (/streamx/*) only.
// The component file is preserved for the page-local mount + tests.
import UniversalProfilePic from '@/components/UniversalProfilePic';
import ActiveSportSidebarWidget from '@/components/layout/ActiveSportSidebarWidget';
// Phase 264 — Pinned "Install Out-Play" CTA next to NotificationBell, visible
// to every role on every page (hides itself when already installed/standalone).
import HeaderInstallButton from '@/components/HeaderInstallButton';
// Phase 264c — First-login mobile onboarding modal for parents + students.
// Self-gates (mobile viewport + role + once-per-device); silent on desktop.
import FirstLoginPwaModal from '@/components/FirstLoginPwaModal';
// Phase 265 — Persona-aware adoption nudge banner. Self-gates: renders
// nothing when the user has no open onboarding tracks or has dismissed
// every track within the 7-day TTL window.
import AdoptionNudgeBanner from '@/components/AdoptionNudgeBanner';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

// Sidebar navigation items grouped by section.
// section "pitch"   = front-line operational tools (top of sidebar)
// section "office"  = back-office / analytics / comms (lower)
// section "admin"   = academy/system administration (bottom — still visible)
// section "support" = help/announcements/tickets (footer area)
// Profile & Security and Settings are intentionally NOT here — they live in the
// top-right avatar dropdown only (Phase 63 refactor).
const navItems = [
  // ===== Academy Owner =====
  { path: '/dashboard',         label: 'Dashboard',          icon: LayoutDashboard, roles: ['academy_owner'], section: 'pitch' },
  { path: '/students',          label: 'Students',           icon: Users,           roles: ['academy_owner'], section: 'pitch' },
  { path: '/hrms',              label: 'Coaches & Staff',    icon: Briefcase,       roles: ['academy_owner'], section: 'pitch' },
  { path: '/staff/scorecards',  label: 'Coach Scorecards',   icon: FileText,        roles: ['academy_owner'], section: 'pitch' },
  { path: '/events',            label: 'Events',             icon: CalendarDays,    roles: ['academy_owner'], section: 'pitch' },
  { path: '/training-sessions', label: 'Training Sessions',  icon: ClipboardCheck,  roles: ['academy_owner'], section: 'pitch' },
  { path: '/drills',            label: 'Drill Library',      icon: Dumbbell,        roles: ['academy_owner'], section: 'pitch' },
  { path: '/session-plans',     label: 'Session Plans',      icon: Layers,          roles: ['academy_owner'], section: 'pitch' },
  { path: '/video-analysis',    label: 'Video Analysis',     icon: Video,           roles: ['academy_owner'], section: 'pitch' },
  { path: '/injuries',          label: 'Injury Tracker',     icon: Heart,           roles: ['academy_owner'], section: 'pitch' },
  { path: '/wall-of-champions', label: 'Wall of Champions',   icon: Trophy,          roles: ['academy_owner'], section: 'pitch', accent: 'amber' },
  { path: '/honors-manager',    label: 'Honors Ledger',      icon: Trophy,          roles: ['academy_owner'], section: 'pitch', accent: 'amber' },
  { path: '/streamx/dashboard',   label: 'StreamX Cockpit',      icon: Radio,           roles: ['academy_owner'], section: 'pitch', accent: 'rose', requiresStreamX: true, requiresLegendTier: true },
  { path: '/streamx/studio',      label: 'Go Live',            icon: Radio,           roles: ['academy_owner'], section: 'pitch', accent: 'rose', requiresStreamX: true, requiresLegendTier: true },
  { path: '/streamx/history',     label: 'Stream Library',     icon: Trophy,          roles: ['academy_owner'], section: 'pitch', accent: 'rose', requiresStreamX: true, requiresLegendTier: true },
  { path: '/streamx/subscription', label: 'StreamX Subscription', icon: Crown,         roles: ['academy_owner'], section: 'pitch', accent: 'rose', requiresStreamX: true, requiresLegendTier: true },

  { path: '/finance',           label: 'Finance',            icon: IndianRupee,     roles: ['academy_owner'], section: 'office' },
  { path: '/kpi',               label: 'KPI Dashboard',      icon: BarChart3,       roles: ['academy_owner'], section: 'office' },
  { path: '/crm',               label: 'CRM',                icon: Target,          roles: ['academy_owner'], section: 'office' },
  { path: '/academy/inbound-crm', label: 'Trial Leads',      icon: Bot,             roles: ['academy_owner'], section: 'office' },
  { path: '/arenax/venue-admin', label: 'ArenaX Venue',      icon: Trophy,          roles: ['academy_owner'], section: 'office', accent: 'cyan', requiresLegendTier: true },
  { path: '/academy-chat',      label: 'Chat',               icon: MessageSquare,   roles: ['academy_owner'], section: 'office' },
  { path: '/message-logs',      label: 'Comms History',      icon: Inbox,           roles: ['academy_owner', 'super_admin'], section: 'office' },

  { path: '/tickets',           label: 'Tickets',            icon: Ticket,          roles: ['academy_owner', 'super_admin', 'employee', 'coach'], section: 'support' },
  { path: '/announcements',     label: 'Broadcast',          icon: Bell,            roles: ['academy_owner', 'super_admin', 'employee', 'coach', 'student', 'parent'], section: 'support' },
  { path: '/help',              label: 'Help Center',        icon: HelpCircle,      roles: ['academy_owner', 'super_admin', 'employee', 'coach', 'student', 'parent'], section: 'support' },

  // ===== Super Admin =====
  { path: '/super-admin',                label: 'Dashboard',      icon: LayoutDashboard, roles: ['super_admin'], section: 'pitch' },
  { path: '/super-admin/academies',      label: 'Academies',      icon: Building2,       roles: ['super_admin'], section: 'pitch' },
  { path: '/super-admin/trials',         label: 'Trial Monitor',  icon: Clock,           roles: ['super_admin'], section: 'pitch' },
  { path: '/super-admin/tiers',          label: 'Tier Manager',   icon: Crown,           roles: ['super_admin'], section: 'pitch' },
  { path: '/super-admin/socialbee',      label: 'SocialBee',      icon: Hexagon,         roles: ['super_admin'], section: 'office', accent: 'amber' },
  // Phase 327 → Phase 605: "AI Sales Employee" deprecated and removed. The new
  //   4-team AI Agency Command Center at /super-admin/agency replaces it.
  // (Lead Intel, Funnel Analytics, Acquisition CRM, CRM Directory, Website Tracking).
  // The old URLs still work (auto-redirect in App.js) for bookmarks + deep-link footer.
  // Phase 550c — AI Agency Command Center (live 4-team telemetry)
  { path: '/super-admin/agency',         label: 'AI Agency',         icon: Workflow,    roles: ['super_admin'], section: 'office', accent: 'emerald' },
  { path: '/super-admin/agency-crm',     label: 'Agency CRM',        icon: Target,      roles: ['super_admin'], section: 'office', accent: 'blue' },
  { path: '/super-admin/arenax',         label: 'ArenaX Ops',     icon: Trophy,          roles: ['super_admin'], section: 'office', accent: 'cyan' },
  { path: '/admin/streamx',                label: 'StreamX Admin',    icon: Radio,           roles: ['super_admin'], section: 'office', accent: 'rose' },
  { path: '/socialbee',                  label: 'SocialBee',      icon: Hexagon,         roles: ['academy_owner'], section: 'office', accent: 'amber', requiresLegend: true },
  { path: '/super-admin/finance',        label: 'Finance',        icon: IndianRupee,     roles: ['super_admin'], section: 'office' },
  { path: '/super-admin/portfolio',      label: 'Portfolio',      icon: BarChart3,       roles: ['super_admin'], section: 'office' },
  { path: '/super-admin/invoices',       label: 'Invoices',       icon: CreditCard,      roles: ['super_admin'], section: 'office' },
  { path: '/super-admin/announce',       label: 'Broadcast',      icon: Megaphone,       roles: ['super_admin'], section: 'office' },
  { path: '/super-admin/webhooks',       label: 'Webhooks',       icon: Webhook,         roles: ['super_admin'], section: 'office', accent: 'indigo' },
  { path: '/super-admin/video-analysis', label: 'Video Analysis', icon: Video,           roles: ['super_admin'], section: 'office' },
  { path: '/super-admin/autonomous',     label: 'Autonomous Ops', icon: Bot,             roles: ['super_admin'], section: 'office', accent: 'violet' },
  { path: '/super-admin/billing-health', label: 'Billing Health', icon: Heart,           roles: ['super_admin'], section: 'office', accent: 'emerald' },
  { path: '/super-admin/adoption',       label: 'Adoption Cockpit', icon: Activity,      roles: ['super_admin'], section: 'office', accent: 'teal' },
  { path: '/super-admin/walkthroughs',   label: 'Walkthroughs',   icon: Video,           roles: ['super_admin'], section: 'office', accent: 'indigo' },

  // ===== Student (Phase 99 — unified ordering: Dashboard → My Portal → modules) =====
  // Phase 250 — "Training Sessions" removed from student sidebar: scheduling
  // calendars already render natively inside the Student Portal + Dashboard,
  // so a dedicated sidebar entry was duplicative. Coach/Owner roles keep it.
  { path: '/student',           label: 'Dashboard',       icon: LayoutDashboard, roles: ['student'], section: 'pitch' },
  { path: '/student/profile',   label: 'My Portal',       icon: User,            roles: ['student'], section: 'pitch' },
  { path: '/student-events',    label: 'Events',          icon: CalendarDays,    roles: ['student'], section: 'pitch' },
  { path: '/student-training',  label: 'Workout',         icon: Dumbbell,        roles: ['student'], section: 'pitch' },
  { path: '/match-analysis',    label: 'Match Analysis',  icon: Trophy,          roles: ['student'], section: 'office' },
  { path: '/nutrition',         label: 'Nutrition',       icon: Apple,           roles: ['student'], section: 'office' },
  { path: '/student-chat',      label: 'Chat',            icon: MessageSquare,   roles: ['student'], section: 'office' },

  // ===== Parent (Phase 99 — unified ordering) =====
  { path: '/parent',         label: 'Dashboard',    icon: LayoutDashboard, roles: ['parent'], section: 'pitch' },
  { path: '/parent/profile', label: 'My Portal',    icon: User,            roles: ['parent'], section: 'pitch' },

  // ===== Employee / Coach (Phase 99 — ACL-gated office links) =====
  { path: '/coach',             label: 'Dashboard',         icon: LayoutDashboard, roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/employee',          label: 'My Portal',         icon: Briefcase,       roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/training-sessions', label: 'Training Sessions', icon: ClipboardCheck,  roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/drills',            label: 'Drill Library',     icon: Dumbbell,        roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/session-plans',     label: 'Session Plans',     icon: Layers,          roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/video-analysis',    label: 'Video Analysis',    icon: Video,           roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/injuries',          label: 'Injury Tracker',    icon: Heart,           roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/events',            label: 'Events',            icon: CalendarDays,    roles: ['employee', 'coach'], section: 'pitch' },
  { path: '/employee-chat',     label: 'Chat',              icon: MessageSquare,   roles: ['employee', 'coach'], section: 'office' },
  // Phase 99 — gated by per-employee ACL. Owner sees them by default; manager/coach
  // only after Academy Admin toggles the matching permission.
  { path: '/students',  label: 'Students',      icon: Users,       roles: ['employee', 'coach'], section: 'office', requiredPermission: 'view_students' },
  { path: '/finance',   label: 'Finance',       icon: IndianRupee, roles: ['employee', 'coach'], section: 'office', requiredPermission: 'view_finance' },
  { path: '/kpi',       label: 'KPI Dashboard', icon: BarChart3,   roles: ['employee', 'coach'], section: 'office', requiredPermission: 'view_kpi' },
  { path: '/crm',       label: 'CRM',           icon: Target,      roles: ['employee', 'coach'], section: 'office', requiredPermission: 'manage_crm' },
];

const SECTION_LABELS = {
  pitch: 'Coaching',
  office: 'Back-Office',
  support: 'Support',
};
const SECTION_ORDER = ['pitch', 'office', 'support'];

export default function AppShell({ children }) {
  const { user, logout, setUser } = useAuth();
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [dark, setDark] = useState(document.documentElement.classList.contains('dark'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [academyLogo, setAcademyLogo] = useState(null);
  const [academy, setAcademy] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [chatContacts, setChatContacts] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  // Phase 89A — Legend-tier feature flags. Drives the conditional SocialBee
  // sidebar entry for academy_owners.
  const [socialbeeAccess, setSocialbeeAccess] = useState(null);
  // Phase 195 — StreamX sidebar gate. Same shape as `socialbeeAccess`.
  // Hides StreamX Cockpit/Go Live/Stream Library/Subscription nav for
  // academies that have never engaged with StreamX, cleaning the
  // sidebar for non-streamers.  Engaged academies (real subscription
  // doc OR historical stream) see the full nav as before.
  const [streamxAccess, setStreamxAccess] = useState(null);
  // Phase 111 — Identity Gateway. List of roles bound to the active email so
  // the avatar dropdown can offer a "Switch Role" submenu without forcing the
  // user to log out and back in.
  const [myRoles, setMyRoles] = useState([]);

  // Phase 610 — Super-Admin profile-picture upload. Hidden <input type=file>
  // triggered from the avatar dropdown. Spinner overlay flips while the
  // upload is in flight; AuthContext is mutated optimistically so the
  // header avatar flips instantly without a /auth/me round-trip.
  const profilePicInputRef = useRef(null);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);

  const handleProfilePicSelect = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input value so selecting the same file twice still fires onChange
    if (e.target) e.target.value = '';
    if (!file) return;
    // Strict client-side 2MB cap per spec — avoids burning a backend
    // round-trip when we already know it'll 400.
    const MAX_BYTES = 2 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast.error('Image must be under 2MB.');
      return;
    }
    if (!/^image\//.test(file.type || '')) {
      toast.error('Please choose an image file (JPEG, PNG, WebP, or GIF).');
      return;
    }
    setUploadingProfilePic(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await api.post('/super-admin/profile-picture', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = resp.data?.profile_photo_url;
      if (newUrl && setUser) {
        // Optimistic local cache mutation — flips the header avatar instantly
        setUser((prev) => prev ? { ...prev, profile_photo_url: newUrl } : prev);
      }
      toast.success('Profile picture updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed. Try again.');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const handleRemoveProfilePic = async () => {
    try {
      await api.delete('/super-admin/profile-picture');
      if (setUser) {
        setUser((prev) => prev ? { ...prev, profile_photo_url: null } : prev);
      }
      toast.success('Profile picture removed.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove picture.');
    }
  };

  // Phase 261i — PostHog Feature Flag gates so super admins can A/B test
  // which install-surface combination converts best per persona. Default
  // `true` everywhere = current behaviour preserved when flags are absent.
  const showBannerDashboard = useFeatureFlag('show_pwa_banner_dashboard', true);
  const showBannerStudent = useFeatureFlag('show_pwa_banner_student', true);

  useEffect(() => {
    api.get('/auth/my-roles').then(r => setMyRoles(r.data?.roles || [])).catch(() => setMyRoles([]));
  }, [user?.id]);

  // Phase 282 — One-shot welcome toast on first login. AuthContext sets the
  // `outplay_first_login` session flag when the backend returns first_login=true
  // in /api/auth/login. Putting the toast here in AppShell means it surfaces
  // for EVERY role (owner / coach / student / parent / employee) on whichever
  // route the user lands on first. Idempotent: flag is cleared on read.
  useEffect(() => {
    if (!user?.id) return;
    let flag = null;
    try { flag = sessionStorage.getItem('outplay_first_login'); } catch (_) { /* ignore */ }
    if (flag !== '1') return;
    try { sessionStorage.removeItem('outplay_first_login'); } catch (_) { /* ignore */ }
    (async () => {
      try {
        const { toast } = await import('sonner');
        setTimeout(() => {
          toast.success(
            "Welcome aboard! Check your inbox — we've sent your role-tailored quickstart guide.",
            { duration: 7000, id: 'first-login-welcome' }
          );
        }, 600);
      } catch (_) { /* sonner unavailable */ }
    })();
  }, [user?.id]);

  const handleSwitchRole = async (targetUserId, targetRole) => {
    try {
      const { data } = await api.post('/auth/switch-role', {
        target_user_id: targetUserId, target_role: targetRole,
      });
      // Mirror the AuthContext login flow: drop token in localStorage and reload.
      localStorage.setItem('outplay_token', data.access_token);
      window.location.assign('/dashboard');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Switch failed';
      // eslint-disable-next-line no-console
      console.error('switch-role', msg);
    }
  };

  useEffect(() => {
    // Fetch once per session — gates the academy_owner SocialBee link.
    api.get('/socialbee/access').then(r => setSocialbeeAccess(r.data)).catch(() => setSocialbeeAccess({ has_access: false }));
    // Phase 195 — fetch StreamX access ONLY for academy_owners.  Other
    // roles never see these nav items anyway, so we skip the network call.
    if (user?.role === 'academy_owner') {
      api.get('/livex/me/access')
        .then(r => setStreamxAccess(r.data))
        .catch(() => setStreamxAccess({ has_access: false }));
    } else {
      setStreamxAccess({ has_access: false });
    }
  }, [user?.role]);

  useEffect(() => {
    api.get('/notifications').then(r => {
      const data = r.data;
      if (data && data.notifications) setNotifications(data.notifications);
      else if (Array.isArray(data)) setNotifications(data);
      else setNotifications([]);
    }).catch(() => {});
    // /academy/* is academy-scoped; super_admin + player have no academy → skip the 404 noise.
    if (user?.role && user.role !== 'super_admin' && user.role !== 'player') {
      api.get('/academy/logo').then(r => { if (r.data?.logo_url) setAcademyLogo(resolvePhotoUrl(r.data.logo_url)); }).catch(() => {});
      api.get('/academy').then(r => setAcademy(r.data)).catch(() => {});
    }
    api.get('/chat/contacts').then(r => setChatContacts(r.data || [])).catch(() => {});
    api.get('/chat/unread-count').then(r => setChatUnread(r.data?.count || 0)).catch(() => {});
    if (user?.role === 'student') {
      api.get('/student/me').then(r => {
        const url = bestPhotoFor(r.data);
        if (url) setUserPhoto(url);
      }).catch(() => {});
    }
    if (user?.role === 'employee' || user?.role === 'coach') {
      api.get('/employee/me').then(r => {
        const url = bestPhotoFor(r.data);
        if (url) setUserPhoto(url);
      }).catch(() => {});
    }
    // Phase 122B — academy_owner gets the academy LOGO in the header avatar
    // (their "brand identity"), NOT their personal profile photo.  Phase 88.2
    // mistakenly fell back to /auth/me's profile_photo_url, which left
    // owners with the silhouette icon even when an academy logo was uploaded.
    // The /academy/logo endpoint resolves directly to academy.logo_url.
    if (user?.role === 'academy_owner' && !userPhoto) {
      api.get('/academy/logo').then(r => {
        const url = resolvePhotoUrl(r.data?.logo_url);
        if (url) setUserPhoto(url);
      }).catch(() => {});
    }
    // Phase 202.bugfix — depend on `user?.role` so the employee/coach photo
    // fetch retries when the auth context hydrates AFTER the initial mount.
    // Previously only `location.pathname` was a dep, so first-load employees
    // saw the silhouette fallback until they navigated to a new page.
  }, [location.pathname, user?.role]);

  // Phase 79 — Live photo refresh when employee uploads a new pic
  useEffect(() => {
    const handler = (e) => {
      const url = resolvePhotoUrl(e.detail?.url);
      if (url) setUserPhoto(url);
    };
    window.addEventListener('outplay:profile-photo-updated', handler);
    return () => window.removeEventListener('outplay:profile-photo-updated', handler);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const toggleDark = () => {
    document.documentElement.classList.toggle('dark');
    setDark(!dark);
  };

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'OP';

  const SidebarContent = ({ mobile = false }) => {
    const visibleItems = navItems.filter(item => {
      if (item.roles && !item.roles.includes(user?.role)) return false;
      // Phase 89A — filter Legend-only items (e.g. SocialBee for academy_owner)
      if (item.requiresLegend && !socialbeeAccess?.has_access) return false;
      // Phase 195 — filter StreamX nav for academies that have never engaged
      if (item.requiresStreamX && !streamxAccess?.has_access) return false;
      // Phase 255f — additionally gate StreamX nav by Out-Play tier. Pro-tier
      // academies (even if they've engaged with LiveX historically) won't see
      // StreamX in the sidebar — Legend tier is the marketed entry point.
      // NOTE: Pro-tier academies that ALREADY paid for StreamX will lose nav
      // access. Confirmed acceptable trade-off in Phase 255f rebrand.
      if (item.requiresLegendTier) {
        const tier = (academy?.tier || '').toLowerCase();
        if (tier !== 'legend' && tier !== 'legends') return false;
      }
      // Phase 99 — ACL gating. Owners / super_admins bypass; everyone else must
      // hold the named permission for the item to render in the sidebar.
      if (item.requiredPermission) {
        const role = user?.role;
        const owners = role === 'academy_owner' || role === 'super_admin';
        const perms = Array.isArray(user?.permissions) ? user.permissions : [];
        if (!owners && !perms.includes(item.requiredPermission)) return false;
      }
      return true;
    });
    // De-dup by path (some roles share paths)
    const seen = new Set();
    const dedup = visibleItems.filter(it => (seen.has(it.path) ? false : (seen.add(it.path), true)));
    const showLabels = !collapsed || mobile;
    const installable = canInstall && !installed && !isStandalone;
    const iosNudge = isIos && !isStandalone && !installed;
    const handleLogoClick = async (e) => {
      e.preventDefault();
      if (installable) {
        const accepted = await promptInstall();
        if (accepted) return;
        // User dismissed — fall through to dashboard nav.
      }
      if (iosNudge) {
        // iOS Safari doesn't support beforeinstallprompt — nudge the user.
        try {
          const { toast } = await import('sonner');
          toast.message('Tap the Share icon → "Add to Home Screen"', {
            description: 'Install Out-Play as an app on iOS',
            duration: 5000,
          });
        } catch (_) { /* sonner unavailable */ }
        return;
      }
      navigate(user ? '/dashboard' : '/');
    };
    const titleHint = installable
      ? '✨ Click logo to install Out-Play on your desktop app dock!'
      : iosNudge
        ? 'Add Out-Play to your iPhone home screen via Share → Add to Home Screen'
        : 'Open Out-Play';
    return (
      <div className="flex flex-col h-full">
        <button
          type="button"
          onClick={handleLogoClick}
          title={titleHint}
          aria-label={titleHint}
          data-testid="outplay-brand-logo"
          className={`flex items-center gap-2 px-4 py-5 border-b border-border w-full text-left transition-colors group ${
            installable ? 'cursor-pointer hover:bg-emerald-500/5' : 'cursor-pointer hover:bg-muted/30'
          }`}
        >
          {/* Phase 600 — B2B2C Co-Branding. Render the academy crest first,
              divider, then Out-Play. Visible only for end-user modules
              (student, parent, employee, coach) so the academy_owner's avatar
              path + super_admin/player no-academy paths stay untouched. Hidden
              when the sidebar is collapsed so the 8x8 mono icon stays tidy. */}
          {academyLogo
            && showLabels
            && (user?.role === 'student'
                || user?.role === 'parent'
                || user?.role === 'employee'
                || user?.role === 'coach') && (
            <>
              <img
                src={academyLogo}
                alt={academy?.name || 'Academy Logo'}
                data-testid="cobrand-academy-logo"
                className="h-8 w-auto max-w-[100px] object-contain rounded-sm flex-shrink-0"
                onError={(e) => {
                  // Gracefully hide both the image AND its sibling divider
                  // if the academy's logo URL is broken (S3/CDN miss, expired
                  // signed link, etc.). Falls back to Out-Play-only branding.
                  e.currentTarget.style.display = 'none';
                  const sib = e.currentTarget.nextElementSibling;
                  if (sib && sib.dataset && sib.dataset.cobrandDivider === 'true') {
                    sib.style.display = 'none';
                  }
                }}
              />
              <div
                data-cobrand-divider="true"
                data-testid="cobrand-divider"
                className="h-6 w-px bg-border flex-shrink-0"
              />
            </>
          )}
          <img src={LOGO_URL} alt="Out-Play" className="h-8 w-8 rounded-md object-contain flex-shrink-0" />
          {showLabels && (
            <div className="flex flex-col min-w-0">
              <BrandText className="text-lg" />
              {installable && (
                <span className="text-[9px] font-bold tracking-wider uppercase text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="outplay-pwa-install-hint">
                  ✨ Tap to install app
                </span>
              )}
            </div>
          )}
        </button>
        {/* Phase 261e — Active Workspace Sport widget. Honest source: reads
            the academy's configured sport from MongoDB via /api/academy/sport-anchor.
            Hidden for students/parents and when no sport is configured. */}
        <ActiveSportSidebarWidget collapsed={collapsed && !mobile} />
        <nav className="flex-1 py-3 overflow-y-auto">
          {SECTION_ORDER.map((section, idx) => {
            const items = dedup.filter(it => (it.section || 'pitch') === section);
            if (items.length === 0) return null;
            return (
              <div key={section} className={idx === 0 ? '' : 'mt-3 pt-3 border-t border-border/40'}>
                {showLabels && (
                  <p className="px-4 mb-1.5 text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/70">
                    {SECTION_LABELS[section]}
                  </p>
                )}
                <div className="space-y-0.5 px-2">
                  {items.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => mobile && setMobileOpen(false)}
                        title={collapsed && !mobile ? item.label : undefined}
                        className={`flex items-center gap-3 h-10 px-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                          isActive
                            ? 'bg-primary/15 text-primary border-l-2 border-primary pl-[10px]'
                            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground border-l-2 border-transparent'
                        } ${collapsed && !mobile ? 'justify-center px-2' : ''}`}
                      >
                        <Icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={isActive ? 2.25 : 1.75} />
                        {showLabels && <span className="truncate">{item.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col border-r border-border bg-card transition-[width] duration-200 ${collapsed ? 'w-16' : 'w-60'}`}>
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center py-3 border-t border-border text-muted-foreground hover:text-foreground transition-colors duration-150"
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Trial Banner */}
        <TrialBanner />
        {/* Suspension Overlay */}
        <SuspensionOverlay />
        {/* Phase 264c — First-login mobile PWA onboarding. Self-gated:
            renders nothing on desktop, for non-student/parent roles, or
            for users who have already seen it on this device. */}
        <FirstLoginPwaModal />

        {/* Phase 265 — Persona-aware Adoption Nudge banner. Renders the
            single highest-priority onboarding/engagement track from
            /api/me/onboarding-health and self-hides when there's
            nothing pending or every track has been dismissed within the
            7-day TTL. */}
        <AdoptionNudgeBanner />
        {/* Phase 255f — LivexGlobalSubscriptionBanner removed from the global
            shell. The page-local LivexSubscriptionStateBanner mounted inside
            LivexDashboard still surfaces StreamX dunning on /streamx/* — that
            single placement is enough for in-context recovery without
            polluting unrelated workspace surfaces. */}
        {/* Top Bar */}
        <header className="flex items-center justify-between h-14 px-4 lg:px-6 border-b border-border bg-card/80 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-toggle">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent mobile />
              </SheetContent>
            </Sheet>
            <h1 className="text-lg font-display font-semibold text-foreground hidden sm:block">
              {location.pathname === '/dashboard' || location.pathname === '/super-admin'
                ? (navItems.find(n => n.path === location.pathname)?.label || 'Dashboard')
                : (user?.role === 'parent' || user?.role === 'student'
                    ? `Welcome, ${formatPersonName(user?.name) || (user?.role === 'parent' ? 'Parent' : 'Student')}`
                    : `Welcome, ${formatPersonName(academy?.name || user?.name) || 'Academy'}`)}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Phase 75 — System Health (super_admin only) */}
            {user?.role === 'super_admin' && <SystemHealthBadge />}
            {/* Phase 264 — Pinned PWA install CTA. Self-hides when not installable. */}
            <HeaderInstallButton />
            <NotificationBell />
            {/* Chat Icon */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="chat-nav-button">
                  <MessageSquare className="h-5 w-5" />
                  {chatUnread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{chatUnread > 9 ? '9+' : chatUnread}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0">
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <div><p className="text-sm font-semibold">Messages</p><p className="text-xs text-muted-foreground">{chatContacts.length} contacts</p></div>
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { const chatPath = user?.role === 'student' ? '/student-chat' : user?.role === 'academy_owner' ? '/academy-chat' : '/student-chat'; navigate(chatPath); }}>Open Chat</Button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {chatContacts.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">No contacts</div>
                  ) : chatContacts.slice(0, 6).map(c => (
                    <button key={c.id} className="w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-border/50 text-left hover:bg-muted/50 transition-colors" onClick={() => { const chatPath = user?.role === 'student' ? '/student-chat' : user?.role === 'academy_owner' ? '/academy-chat' : '/student-chat'; navigate(chatPath); }}>
                      <div className="relative flex-shrink-0">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${c.type === 'employee' ? 'bg-blue-500' : c.type === 'admin' ? 'bg-emerald-600' : 'bg-purple-500'}`}>{c.name?.split(' ').map(n => n[0]).join('').slice(0,2)}</div>
                        <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card ${c.online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium truncate">{c.name}</p>
                          {c.unread > 0 && <span className="h-4 min-w-[16px] px-1 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center">{c.unread}</span>}
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate">{c.last_message ? (c.last_is_mine ? 'You: ' : '') + c.last_message : c.role}</p>
                      </div>
                    </button>
                  ))}
                  {chatContacts.length > 6 && <button className="w-full py-2 text-xs text-primary font-medium hover:bg-muted/50" onClick={() => { const chatPath = user?.role === 'student' ? '/student-chat' : '/academy-chat'; navigate(chatPath); }}>View all {chatContacts.length} contacts →</button>}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="theme-toggle">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {/* Phase 111.1 — Identity Posture pill. Shows the active role
                inline next to the avatar; a green dot indicates additional
                role bindings are available for one-click switching. Hidden on
                very narrow screens to preserve mobile header layout. */}
            {user?.role && (
              <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 border border-border text-xs"
                   data-testid="identity-posture-pill"
                   title={myRoles.length > 1 ? `${myRoles.length} roles available — click avatar to switch` : 'Active workspace'}>
                {myRoles.length > 1 && (
                  <span className="relative flex h-2 w-2" data-testid="identity-posture-swap-dot">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                )}
                <span className="font-semibold capitalize" data-testid="identity-posture-role">
                  {user.role.replace('_', ' ')}
                </span>
                {myRoles.length > 1 && (
                  <span className="text-muted-foreground hidden lg:inline" data-testid="identity-posture-count">
                    · {myRoles.length} roles
                  </span>
                )}
              </div>
            )}
            {/* Phase 610 — hidden file input that the avatar dropdown's
                "Change profile picture" item triggers via ref. accept="image/*"
                so the OS file picker scopes itself to images. */}
            <input
              ref={profilePicInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfilePicSelect}
              data-testid="profile-pic-file-input"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="user-menu-trigger">
                  {/* Phase 122 — Header avatar priority chain:
                      academy_owner → academy.logo_url || user.profile_photo_url
                      everyone else → user.profile_photo_url
                      Silhouette fallback (NOT initials) for clean enterprise feel. */}
                  {/* Phase 202.bugfix — Resolve raw `/api/files/…` paths
                      through `resolvePhotoUrl` so the JWT token query-arg is
                      attached. Without this, the <img> request omits the
                      Authorization header AND the token query-param, so the
                      backend rejects it and the silhouette fallback shows
                      for employees/coaches. For non-owners, prefer the
                      already-resolved `userPhoto` (populated from
                      /employee/me or /student/me) and fall back to the raw
                      user.profile_photo_url wrapped in resolvePhotoUrl. */}
                  <UniversalProfilePic
                    name={user?.name}
                    entity={user}
                    photoUrl={
                      user?.role === 'academy_owner'
                        ? (userPhoto || resolvePhotoUrl(user?.profile_photo_url))
                        : (userPhoto || resolvePhotoUrl(user?.profile_photo_url) || resolvePhotoUrl(user?.employee_profile?.avatar_url))
                    }
                    size="h-9 w-9"
                    fallback="silhouette"
                    testId="header-avatar-badge"
                  />
                  {/* Phase 610 — upload spinner overlay (super_admin only). */}
                  {uploadingProfilePic && user?.role === 'super_admin' && (
                    <span
                      className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40"
                      data-testid="avatar-upload-spinner"
                    >
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                  <Shield className="mr-2 h-4 w-4" /> Profile &amp; Security
                </DropdownMenuItem>
                {(user?.role === 'academy_owner' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                      <Settings className="mr-2 h-4 w-4" /> Settings
                    </DropdownMenuItem>
                    {user?.role === 'academy_owner' && (
                      <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="menu-subscription">
                        <CreditCard className="mr-2 h-4 w-4" /> Subscription
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {/* Phase 610 — Super-Admin profile picture upload / remove. */}
                {user?.role === 'super_admin' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(ev) => { ev.preventDefault(); profilePicInputRef.current?.click(); }}
                      disabled={uploadingProfilePic}
                      data-testid="menu-change-profile-pic"
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      {uploadingProfilePic ? 'Uploading…' : (user?.profile_photo_url ? 'Change profile picture' : 'Upload profile picture')}
                    </DropdownMenuItem>
                    {user?.profile_photo_url && (
                      <DropdownMenuItem
                        onClick={handleRemoveProfilePic}
                        disabled={uploadingProfilePic}
                        data-testid="menu-remove-profile-pic"
                        className="text-muted-foreground"
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Remove profile picture
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
                {/* Phase 111 — Switch Role quick-toggle. Only renders when the
                    authenticated email has multiple active role memberships. */}
                {myRoles.length > 1 && (
                  <>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Switch role</div>
                    {myRoles.filter(r => !r.is_active_session).map((r) => (
                      <DropdownMenuItem key={r.user_id}
                                        onClick={() => handleSwitchRole(r.user_id, r.role)}
                                        data-testid={`menu-switch-role-${r.role}`}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        <span className="capitalize">Continue as {r.role.replace('_', ' ')}</span>
                        {r.academy_name && (
                          <span className="ml-2 text-[10px] text-muted-foreground truncate">· {r.academy_name}</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Phase 119 — On-login PWA install banner.
                Phase 261 — Now also surfaces on student landing page so
                students/parents (mobile-first persona) see the install nudge.
                Phase 261i — PostHog feature flags `show_pwa_banner_dashboard`
                and `show_pwa_banner_student` let super admins A/B test the
                banner per route without code changes. Default = true.
                Auto-hides when: installed / standalone / dismissed / unsupported. */}
            {location.pathname === '/dashboard' && showBannerDashboard && <PwaInstallBanner />}
            {location.pathname === '/student' && showBannerStudent && <PwaInstallBanner />}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
